import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent,
  Select,
  MenuItem,
  FormControl,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  alpha,
  Pagination,
  TableSortLabel,
  Paper,
  CircularProgress,
  Collapse,
  Button,
  IconButton,
  Fade,
  Divider,
  InputLabel,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CardHeader,
  Badge,
  Grow,
  Fab,
  LinearProgress
} from '@mui/material';
import { motion } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import LoadingScreen from './LoadingScreen';
import { visuallyHidden } from '@mui/utils';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import DescriptionIcon from '@mui/icons-material/Description';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArticleIcon from '@mui/icons-material/Article';
import { styled, useTheme } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import PageWrapper from './PageWrapper';
import SubjectIcon from '@mui/icons-material/Subject';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import { format } from 'date-fns';
import FeedbackIcon from '@mui/icons-material/Feedback';
import AssessmentIcon from '@mui/icons-material/Assessment';
import InsightsIcon from '@mui/icons-material/Insights';
import RecommendIcon from '@mui/icons-material/Recommend';
import BarChartIcon from '@mui/icons-material/BarChart';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Zoom from '@mui/material/Zoom';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const createGradientPlugin = {
  id: 'customCanvasBackgroundColor',
  beforeDraw: (chart, args, options) => {
    const {ctx} = chart;
    const chartArea = chart.chartArea;
    const datasets = chart.data.datasets;

    if (!chartArea) {
      return;
    }

    datasets.forEach((dataset, i) => {
      const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
      if (i === 0) { // Total Calls
        gradient.addColorStop(0, 'rgba(10, 132, 255, 0)');
        gradient.addColorStop(1, 'rgba(10, 132, 255, 0.2)');
      } else if (i === 1) { // Leads
        gradient.addColorStop(0, 'rgba(52, 199, 89, 0)');
        gradient.addColorStop(1, 'rgba(52, 199, 89, 0.2)');
      } else { // Enquiries
        gradient.addColorStop(0, 'rgba(88, 86, 214, 0)');
        gradient.addColorStop(1, 'rgba(88, 86, 214, 0.2)');
      }
      dataset.backgroundColor = gradient;
    });
  }
};

const SummaryButton = styled(Button)(({ theme }) => ({
  borderRadius: 20,
  textTransform: 'none',
  padding: '6px 16px',
  fontSize: '0.875rem',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: theme.shadows[2],
  },
}));

const SummaryContent = styled(Paper)(({ theme }) => ({
  margin: theme.spacing(2),
  padding: theme.spacing(2),
  background: theme.palette.background.default,
  borderRadius: theme.spacing(1),
  boxShadow: 'none',
  border: `1px solid ${theme.palette.divider}`,
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -8,
    left: 20,
    width: 16,
    height: 16,
    background: theme.palette.background.default,
    transform: 'rotate(45deg)',
    borderLeft: `1px solid ${theme.palette.divider}`,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const SummarySection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '&:last-child': {
    marginBottom: 0
  },
  '& .section-header': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: theme.spacing(1),
    transition: 'all 0.2s ease-in-out',
    cursor: 'default',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.05),
      '& .MuiSvgIcon-root': {
        color: theme.palette.primary.main,
        transform: 'scale(1.1)',
      }
    },
    '& .MuiSvgIcon-root': {
      transition: 'all 0.2s ease-in-out',
      color: theme.palette.text.secondary
    }
  },
  '& .section-content': {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(4),
    '& ul': {
      margin: 0,
      paddingLeft: theme.spacing(2)
    }
  }
}));

const StatCard = ({ title, value, trend, trendLabel, icon, color, suffix }) => {
  const theme = useTheme();
  
  // Helper function to determine trend color
  const getTrendColor = (trendValue) => {
    if (trendValue === 0) return theme.palette.text.secondary;
    return trendValue > 0 ? theme.palette.success.main : theme.palette.error.main;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
      style={{ width: '100%' }}
    >
      <Card
        component={motion.div}
        whileHover={{ y: -2, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}
        transition={{ duration: 0.2 }}
        sx={{ p: 2.5, height: '100%', width: '100%' }}
      >
        <Typography 
          color="text.secondary" 
          variant="body2"
          sx={{ 
            fontSize: '0.75rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            opacity: 0.7
          }}
        >
        {title}
      </Typography>
        <Typography 
          variant="h4" 
          sx={{ 
            my: 1,
            fontSize: '1.75rem',
            fontWeight: 700
          }}
        >
          {
            // Handle NaN values explicitly
            isNaN(value) ? "0.0%" :
            title.includes('Rate') ? 
              value === 0 || value === "0" ? "0.0%" : 
              `${(typeof value === 'number' ? value : Number(value)).toFixed(1)}%` :
            title.includes('Cost') ? 
              value === 0 || value === "0" ? "$0.00" :
              `$${(typeof value === 'number' ? value : Number(value)).toFixed(2)}` :
            title.includes('Duration') ? 
              value === 0 || value === "0" ? "0.0s" :
              `${(typeof value === 'number' ? value : Number(value)).toFixed(1)}s` :
            value
          }
        </Typography>
        {trend !== undefined && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mt: 0.5,
            color: getTrendColor(trend)
          }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              px: 1,
              py: 0.25,
              borderRadius: 1,
              bgcolor: alpha(getTrendColor(trend), 0.1),
              fontSize: '0.75rem',
              fontWeight: 'bold'
            }}>
              {trend > 0 ? <TrendingUpIcon fontSize="inherit" sx={{ mr: 0.5 }} /> : <TrendingDownIcon fontSize="inherit" sx={{ mr: 0.5 }} />}
              {trend > 0 ? '↑' : '↓'} {Math.abs(typeof trend === 'number' ? trend : Number(trend)).toFixed(1)}%
            </Box>
          </Box>
        )}
        {trendLabel && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mt: 0.5,
            color: getTrendColor(trend)
          }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.75rem',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {trend !== 0 ? (
                <>
                  {trend > 0 ? '↑' : '↓'} {Math.abs(typeof trend === 'number' ? trend : Number(trend)).toFixed(1)}%
                </>
              ) : (
                '0%'
              )}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                ml: 0.5,
                fontSize: '0.75rem',
                color: theme.palette.text.secondary
              }}
            >
              vs previous week
            </Typography>
          </Box>
        )}
  </Card>
    </motion.div>
  );
};

const LoadingButton = ({ loading, children, ...props }) => {
  const theme = useTheme();
  
  return (
    <Button
      {...props}
      disabled={loading}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        '&::before': loading ? {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(90deg, 
            transparent 0%,
            ${alpha(theme.palette.primary.main, 0.2)} 50%,
            transparent 100%)`,
          backgroundSize: '200% 100%',
          animation: 'glowingEffect 2s linear infinite',
          zIndex: 0
        } : {},
        '@keyframes glowingEffect': {
          '0%': {
            backgroundPosition: '-200% 0'
          },
          '100%': {
            backgroundPosition: '200% 0'
          }
        },
        ...props.sx
      }}
    >
      <Box sx={{ 
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        {children}
      </Box>
    </Button>
  );
};

const RecentCallRow = ({ call }) => {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState('');
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [structuredSummary, setStructuredSummary] = useState(null);
  const [buttonLoading, setButtonLoading] = useState(false);
  const theme = useTheme();
  
  // Format the date
  const formattedDate = new Date(call.datetime).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  // Format duration to minutes and seconds
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'Lead':
        return {
          bgcolor: 'success.soft',
          color: 'success.main',
          chipColor: 'success'
        };
      case 'Enquiry':
        return {
          bgcolor: 'info.soft',
          color: 'info.main',
          chipColor: 'info'
        };
      default:
        return {
          bgcolor: 'grey.100',
          color: 'text.secondary',
          chipColor: 'default'
        };
    }
  };

  const handleSummaryClick = async () => {
    if (!showSummary) {
      setSummaryLoading(true);
      setButtonLoading(true);
      try {
        const summaryResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/api/calls/${call.id}/generate-summary`,
          {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
            }
          }
        );
            
            if (summaryResponse.ok) {
              const summaryData = await summaryResponse.json();
          setStructuredSummary(summaryData.summary);
        }
      } catch (error) {
        console.error('Error handling summary:', error);
      } finally {
        setSummaryLoading(false);
        setButtonLoading(false);
      }
    }
    setShowSummary(!showSummary);
  };

  const renderSummary = (summary) => (
    <SummaryContent>
      {summary.Onderwerp && (
        <SummarySection>
          <Box className="section-header">
            <SubjectIcon />
            <Typography variant="subtitle1" fontWeight="medium">
              Onderwerp
            </Typography>
          </Box>
          <Box className="section-content">
            <Typography>{summary.Onderwerp}</Typography>
          </Box>
        </SummarySection>
      )}
      
      {summary.Klantinformatie && (
        <SummarySection>
          <Box className="section-header">
            <PersonIcon />
            <Typography variant="subtitle1" fontWeight="medium">
              Klantinformatie
            </Typography>
          </Box>
          <Box className="section-content">
            {typeof summary.Klantinformatie === 'object' ? (
              Object.entries(summary.Klantinformatie).map(([key, value]) => (
                <Typography key={key}>
                  <strong>{key}:</strong> {value}
                </Typography>
              ))
            ) : (
              <Typography>{summary.Klantinformatie}</Typography>
            )}
          </Box>
        </SummarySection>
      )}
      
      {summary.Afspraken && summary.Afspraken.length > 0 && (
        <SummarySection>
          <Box className="section-header">
            <EventIcon />
            <Typography variant="subtitle1" fontWeight="medium">
              Afspraken
            </Typography>
          </Box>
          <Box className="section-content">
            <ul>
              {Array.isArray(summary.Afspraken) 
                ? summary.Afspraken.map((item, index) => (
                    <li key={index}><Typography>{item}</Typography></li>
                  ))
                : <li><Typography>{summary.Afspraken}</Typography></li>
              }
            </ul>
          </Box>
        </SummarySection>
      )}
      
      {summary.Vragen && summary.Vragen.length > 0 && (
        <SummarySection>
          <Box className="section-header">
            <QuestionAnswerIcon />
            <Typography variant="subtitle1" fontWeight="medium">
              Vragen
            </Typography>
          </Box>
          <Box className="section-content">
            <ul>
              {Array.isArray(summary.Vragen)
                ? summary.Vragen.map((item, index) => (
                    <li key={index}><Typography>{item}</Typography></li>
                  ))
                : <li><Typography>{summary.Vragen}</Typography></li>
              }
            </ul>
          </Box>
        </SummarySection>
      )}
    </SummaryContent>
  );

  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{formattedDate}</TableCell>
        <TableCell>
          <Chip 
            label={call.call_type || 'Call'} 
            size="small"
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              fontWeight: 500
            }}
          />
        </TableCell>
        <TableCell>{call.agent_name || 'Unknown'}</TableCell>
        <TableCell>{call.topic || 'No topic'}</TableCell>
        <TableCell>
          <Chip
            label={call.completed ? "Completed" : "Incomplete"}
            color={call.completed ? "success" : "warning"}
            size="small"
            sx={{
              fontWeight: 500,
              '&.MuiChip-colorSuccess': {
                backgroundColor: alpha('#4caf50', 0.1),
                color: '#2e7d32'
              },
              '&.MuiChip-colorWarning': {
                backgroundColor: alpha('#ff9800', 0.1),
                color: '#ed6c02'
              }
            }}
          />
        </TableCell>
        <TableCell align="right">{formatDuration(call.duration)}</TableCell>
        <TableCell align="right">${call.cost ? Number(call.cost).toFixed(2) : '0.00'}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              {(call.transcript || transcript) && (
                <LoadingButton
                  startIcon={<ArticleIcon />}
                  onClick={handleSummaryClick}
                  loading={buttonLoading}
                  variant="outlined"
                  size="small"
                  sx={{ 
                    mb: 2,
                    minWidth: 120
                  }}
                >
                  {showSummary ? 'Hide Summary' : 'Show Summary'}
                </LoadingButton>
              )}

              {showSummary && (
                <Fade in timeout={800}>
                  <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
                    {summaryLoading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} thickness={4} />
                        <Typography variant="body2" color="text.secondary">
                          Analyzing conversation...
                        </Typography>
                      </Box>
                    ) : structuredSummary ? (
                      renderSummary(structuredSummary)
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {call.transcript || 'No transcript available'}
                      </Typography>
                    )}
                  </Box>
                </Fade>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const CallSummary = ({ summary }) => {
  if (!summary) return null;

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        {/* Onderwerp Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" color="primary" gutterBottom>
            Onderwerp van Gesprek
          </Typography>
          <Typography variant="body1">
            {summary.onderwerp}
          </Typography>
        </Box>

        {/* Klantgegevens Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" color="primary" gutterBottom>
            Klantgegevens
          </Typography>
          <Typography variant="body1">
            {summary.klantgegevens}
          </Typography>
        </Box>

        {/* Vragen Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" color="primary" gutterBottom>
            Gestelde Vragen
          </Typography>
          <List>
            {summary.vragenGesteld.map((vraag, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <QuestionAnswerIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary={vraag} />
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Afspraak Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" color="primary" gutterBottom>
            Afspraak Details
          </Typography>
          <Typography variant="body1">
            {summary.afspraakGemaakt}
          </Typography>
        </Box>

        {/* Gesprek Afgerond Section */}
        <Box>
          <Typography variant="h6" color="primary" gutterBottom>
            Gesprek Status
          </Typography>
          <Chip
            label={summary.gesprekAfgerond ? "Gesprek Afgerond" : "Gesprek Niet Afgerond"}
            color={summary.gesprekAfgerond ? "success" : "warning"}
            icon={summary.gesprekAfgerond ? <CheckCircleIcon /> : <ErrorIcon />}
          />
        </Box>
      </Paper>
    </Box>
  );
};

const formatChartData = (data, timeRange) => {
  // Special handling for 24-hour view
  if (timeRange === '1') {
    // Create 24 hourly slots for the last 24 hours
    const now = new Date();
    const labels = Array.from({length: 24}, (_, i) => {
      const d = new Date(now);
      d.setHours(d.getHours() - (23 - i));
      return d;
    });

    // Initialize empty data arrays with 24 zero values
    const totalCallsData = new Array(24).fill(0);
    const leadsData = new Array(24).fill(0);
    const enquiriesData = new Array(24).fill(0);

    // Map the actual data to the correct hour slots
    if (data.datasets) {
      data.datasets[0].data.forEach((value, index) => {
        const dataDate = new Date(data.labels[index]);
        const hourIndex = labels.findIndex(label => 
          label.getHours() === dataDate.getHours() &&
          label.getDate() === dataDate.getDate()
        );
        if (hourIndex !== -1) {
          totalCallsData[hourIndex] = value;
          leadsData[hourIndex] = data.datasets[1].data[index] || 0;
          enquiriesData[hourIndex] = data.datasets[2].data[index] || 0;
        }
      });
    }

  return {
      labels: labels,
    datasets: [
      {
        ...data.datasets[0],
          data: totalCallsData,
        fill: true,
        borderWidth: 1.5,
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 300);
          gradient.addColorStop(0, alpha('rgb(10, 132, 255)', 0.1));
          gradient.addColorStop(1, alpha('rgb(10, 132, 255)', 0));
          return gradient;
        }
      },
      {
        ...data.datasets[1],
          data: leadsData,
        fill: true,
        borderWidth: 1.5,
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 300);
          gradient.addColorStop(0, alpha('rgb(52, 199, 89)', 0.1));
          gradient.addColorStop(1, alpha('rgb(52, 199, 89)', 0));
          return gradient;
        }
      },
      {
        ...data.datasets[2],
          data: enquiriesData,
        fill: true,
        borderWidth: 1.5,
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 300);
          gradient.addColorStop(0, alpha('rgb(88, 86, 214)', 0.1));
          gradient.addColorStop(1, alpha('rgb(88, 86, 214)', 0));
          return gradient;
        }
      }
    ]
  };
  }

  // For other time ranges, use the existing format
  return {
    labels: data.labels,
    datasets: data.datasets.map((dataset, i) => ({
      ...dataset,
      fill: true,
      borderWidth: 1.5,
      backgroundColor: (context) => {
        const ctx = context.chart.ctx;
        const gradient = ctx.createLinearGradient(0, 0, 0, 300);
        if (i === 0) { // Total Calls
          gradient.addColorStop(0, alpha('rgb(10, 132, 255)', 0.1));
          gradient.addColorStop(1, alpha('rgb(10, 132, 255)', 0));
        } else if (i === 1) { // Leads
          gradient.addColorStop(0, alpha('rgb(52, 199, 89)', 0.1));
          gradient.addColorStop(1, alpha('rgb(52, 199, 89)', 0));
        } else { // Enquiries
          gradient.addColorStop(0, alpha('rgb(88, 86, 214)', 0.1));
          gradient.addColorStop(1, alpha('rgb(88, 86, 214)', 0));
        }
        return gradient;
      }
    }))
  };
};

const FeedbackButton = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    topic: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          topic: formData.topic,
          message: formData.message,
          recipient: 'kirubel_mulugeta@hotmail.com' // This will be handled securely in backend
        })
      });

      if (response.ok) {
        setShowSuccess(true);
        setOpen(false);
        setFormData({ topic: '', message: '' });
      }
    } catch (error) {
      console.error('Error sending feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Zoom in={true}>
        <Fab
          color="primary"
          aria-label="feedback"
          sx={{
            position: 'fixed',
            bottom: 32,
            right: 32,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'scale(1.1)',
              boxShadow: (theme) => theme.shadows[8]
            }
          }}
          onClick={() => setOpen(true)}
        >
          <FeedbackIcon />
        </Fab>
      </Zoom>

      <Dialog 
        open={open} 
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Feedback</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Topic"
              fullWidth
              variant="outlined"
              value={formData.topic}
              onChange={(e) => setFormData(prev => ({ ...prev, topic: e.target.value }))}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Message"
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              value={formData.message}
              onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
              required
            />
          </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 0 }}>
            <Button 
              onClick={() => setOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={loading}
            >
              Send Feedback
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowSuccess(false)} 
          severity="success"
          variant="filled"
        >
          Feedback sent successfully!
        </Alert>
      </Snackbar>
    </>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [assignedAgents, setAssignedAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState('all'); // 'all' or specific agent ID
  const [dashboardData, setDashboardData] = useState({
    totalCalls: 0,
    averageDuration: 0,
    // ... other metrics
  });
  const [timeRange, setTimeRange] = useState('7');
  const [stats, setStats] = useState({
    totalCalls: 0,
    successRate: 0,
    avgDuration: 0,
    costPerCall: 0,
    totalLeads: 0,
    totalEnquiries: 0,
    avgCallsPerAgent: 0,
    revenuePerCall: 0,
    trends: {},
    chartData: [],
    avgExtractionDuration: 0,
    extractionDurationTrend: 0
  });
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Total Calls',
        data: [],
        borderColor: '#0A84FF',
        backgroundColor: function(context) {
          const chart = context.chart;
          const {ctx, chartArea} = chart;
          
          if (!chartArea) {
            return null;
          }
          
          const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
          gradient.addColorStop(0, 'rgba(10, 132, 255, 0)');
          gradient.addColorStop(1, 'rgba(10, 132, 255, 0.2)');
          return gradient;
        },
        tension: 0.4,
        fill: true,
        pointBackgroundColor: '#0A84FF'
      },
      {
        label: 'Leads',
        data: [],
        borderColor: '#34C759',
        backgroundColor: 'rgba(52, 199, 89, 0.1)',
        tension: 0.4,
        fill: true,
        pointBackgroundColor: '#34C759'
      },
      {
        label: 'Enquiries',
        data: [],
        borderColor: '#5856D6',
        backgroundColor: 'rgba(88, 86, 214, 0.1)',
        tension: 0.4,
        fill: true,
        pointBackgroundColor: '#5856D6'
      }
    ]
  });
  const [calls, setCalls] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadingCalls, setIsLoadingCalls] = useState(false);
  const [isLoadingChart, setIsLoadingChart] = useState(false);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('datetime');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentCalls, setRecentCalls] = useState([]);
  const [recentCallsPage, setRecentCallsPage] = useState(1);
  const [totalRecentCalls, setTotalRecentCalls] = useState(0);
  const [totalCalls, setTotalCalls] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [summariesCount, setSummariesCount] = useState(0);
  const [currentCallId, setCurrentCallId] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [processingDetails, setProcessingDetails] = useState([]);
  const [completionRate, setCompletionRate] = useState(0);
  const [completionTrend, setCompletionTrend] = useState(0);

  // New state variables for summary generator
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [summaryGenerating, setSummaryGenerating] = useState(false);
  const [summaryProgress, setSummaryProgress] = useState(0);
  const [summaryStatusMessage, setSummaryStatusMessage] = useState('');
  const [summaryProcessingDetails, setSummaryProcessingDetails] = useState([]);
  const [generatedSummary, setGeneratedSummary] = useState('');
  const [summaryStartDate, setSummaryStartDate] = useState(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)); // 7 days ago
  const [summaryEndDate, setSummaryEndDate] = useState(new Date());

  useEffect(() => {
    fetchAssignedAgents();
  }, [user]);

  useEffect(() => {
    fetchDashboardData();
  }, [selectedAgentId]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsInitialLoading(true);
        console.log('Fetching initial data for agent:', selectedAgentId);
        
        const url = selectedAgentId === 'all'
          ? `${process.env.REACT_APP_API_URL}/api/calls/stats`
          : `${process.env.REACT_APP_API_URL}/api/calls/stats/${selectedAgentId}`;

        console.log('Fetching from URL:', url);

        const statsResponse = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!statsResponse.ok) {
          throw new Error(`HTTP error! status: ${statsResponse.status}`);
        }
        
        const statsData = await statsResponse.json();
        console.log('Received stats data:', statsData);
        
        // Validate and transform stats data
        const validatedStats = {
          totalCalls: Number(statsData.totalCalls) || 0,
          successRate: Number(statsData.successRate) || 0,
          avgDuration: Number(statsData.avgDuration) || 0,
          costPerCall: Number(statsData.costPerCall) || 0,
          totalLeads: Number(statsData.totalLeads) || 0,
          totalEnquiries: Number(statsData.totalEnquiries) || 0,
          avgCallsPerAgent: Number(statsData.avgCallsPerAgent) || 0,
          completionRate: isNaN(Number(statsData.completionRate)) ? 0 : Number(statsData.completionRate),
          trends: {
            totalCallsTrend: Number(statsData.trends?.totalCallsTrend) || 0,
            successRateTrend: Number(statsData.trends?.successRateTrend) || 0,
            avgDurationTrend: Number(statsData.trends?.avgDurationTrend) || 0,
            costPerCallTrend: Number(statsData.trends?.costPerCallTrend) || 0,
            totalLeadsTrend: Number(statsData.trends?.totalLeadsTrend) || 0,
            totalEnquiriesTrend: Number(statsData.trends?.totalEnquiriesTrend) || 0,
            avgCallsPerAgentTrend: Number(statsData.trends?.avgCallsPerAgentTrend) || 0,
            completionRateTrend: isNaN(Number(statsData.trends?.completionRateTrend)) ? 0 : Number(statsData.trends?.completionRateTrend)
          },
          recentCalls: statsData.recentCalls || { calls: [], total: 0, pages: 1 },
          avgExtractionDuration: Number(statsData.avgExtractionDuration) || 0,
          extractionDurationTrend: Number(statsData.extractionDurationTrend) || 0
        };

        console.log('Validated stats:', validatedStats);
        setStats(validatedStats);
        setRecentCalls(validatedStats.recentCalls.calls);
        setTotalRecentCalls(validatedStats.recentCalls.total);
        setTotalPages(validatedStats.recentCalls.pages);
        
      } catch (error) {
        console.error('Failed to fetch stats data:', error);
        // Set default values on error
        setStats({
          totalCalls: 0,
          successRate: 0,
          avgDuration: 0,
          costPerCall: 0,
          totalLeads: 0,
          totalEnquiries: 0,
          avgCallsPerAgent: 0,
          completionRate: 0,
          trends: {
            totalCallsTrend: 0,
            successRateTrend: 0,
            avgDurationTrend: 0,
            costPerCallTrend: 0,
            totalLeadsTrend: 0,
            totalEnquiriesTrend: 0,
            avgCallsPerAgentTrend: 0,
            completionRateTrend: 0
          },
          recentCalls: { calls: [], total: 0, pages: 1 },
          avgExtractionDuration: 0,
          extractionDurationTrend: 0
        });
      } finally {
        setTimeout(() => setIsInitialLoading(false), 1500);
      }
    };

    fetchInitialData();
  }, [selectedAgentId]);

  const fetchCalls = useCallback(async () => {
    if (!isInitialLoad) {
      setIsLoadingCalls(true);
    }
    try {
      const baseUrl = `${process.env.REACT_APP_API_URL}/api/calls`;
      const url = selectedAgentId === 'all' 
        ? `${baseUrl}?page=${page}&per_page=${rowsPerPage}`
        : `${baseUrl}?page=${page}&per_page=${rowsPerPage}&agent_id=${selectedAgentId}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      if (response.status === 429) {
        console.warn('Rate limit exceeded. Please wait before making more requests.');
        return;
      }

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const data = await response.json();
      console.log('Received calls data:', data);
      
      const calculatedTotalPages = Math.max(1, Math.ceil(data.total / rowsPerPage));
      setTotalPages(calculatedTotalPages);
      setTotalCalls(data.total);
      setCalls(data.calls || []);
    } catch (error) {
      console.error('Error fetching calls:', error);
      // Add user-friendly error handling
      if (error.message.includes('Failed to fetch')) {
        console.error('Network error - please check your connection');
      }
    } finally {
      setIsLoadingCalls(false);
      setIsInitialLoad(false);
    }
  }, [page, rowsPerPage, selectedAgentId, isInitialLoad]);

  const fetchChartData = useCallback(async () => {
    if (!isInitialLoad) {
      setIsLoadingChart(true);
    }
    try {
      const baseUrl = `${process.env.REACT_APP_API_URL}/api/calls/chart-data`;
      const url = selectedAgentId === 'all' 
        ? `${baseUrl}?days=${timeRange}`
        : `${baseUrl}?days=${timeRange}&agent_id=${selectedAgentId}`;

      console.log('Fetching chart data from URL:', url);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      if (response.status === 429) {
        console.warn('Rate limit exceeded. Please wait before making more requests.');
        return;
      }

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const data = await response.json();
      console.log('Raw chart data from API:', data);
      
      // Validate the received data structure
      if (!data || !data.labels || !Array.isArray(data.labels) || 
          !data.datasets || !Array.isArray(data.datasets)) {
        console.error('Invalid chart data format received:', data);
        setChartData({
          labels: [],
          datasets: [
            {
              label: 'Total Calls',
              data: [],
              borderColor: '#0A84FF',
              tension: 0.4,
              fill: true
            },
            {
              label: 'Leads',
              data: [],
              borderColor: '#34C759',
              tension: 0.4,
              fill: true
            },
            {
              label: 'Enquiries',
              data: [],
              borderColor: '#5856D6',
              tension: 0.4,
              fill: true
            }
          ]
        });
        return;
      }
      
      // Ensure all datasets are present and have proper data
      const validatedData = {
        labels: data.labels || [],
        datasets: []
      };
      
      // Make sure we have all three datasets
      const datasetLabels = ['Total Calls', 'Leads', 'Enquiries'];
      const datasetColors = ['#0A84FF', '#34C759', '#5856D6'];
      
      for (let i = 0; i < 3; i++) {
        const existingDataset = data.datasets[i] || {};
        validatedData.datasets.push({
          label: existingDataset.label || datasetLabels[i],
          data: existingDataset.data || Array(data.labels.length).fill(0),
          borderColor: existingDataset.borderColor || datasetColors[i],
          tension: 0.4,
          fill: true
        });
      }
      
      console.log('Validated data before formatting:', validatedData);
      
      // Check if we're getting empty or near-empty data when we shouldn't be
      const hasData = validatedData.datasets.some(ds => 
        ds.data.some(val => val > 0)
      );
      
      if (!hasData) {
        console.warn('Chart data appears to be empty. Fetching diagnostic data...');
        try {
          const diagnosticUrl = `${process.env.REACT_APP_API_URL}/api/diagnostic/chart-data`;
          const diagnosticResponse = await fetch(diagnosticUrl, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            },
            credentials: 'include'
          });
          
          if (diagnosticResponse.ok) {
            const diagnosticData = await diagnosticResponse.json();
            console.log('Chart data diagnostic information:', diagnosticData);
          }
        } catch (diagnosticError) {
          console.error('Error fetching diagnostic data:', diagnosticError);
        }
      }
      
      const formattedData = formatChartData(validatedData, timeRange);
      console.log('Transformed chart data:', formattedData);
      
      console.log('Dataset lengths:', {
        total: formattedData.datasets[0]?.data?.length || 0,
        leads: formattedData.datasets[1]?.data?.length || 0,
        enquiries: formattedData.datasets[2]?.data?.length || 0
      });
      
      setChartData(formattedData);
    } catch (error) {
      console.error('Error fetching chart data:', error);
      // Add user-friendly error handling
      if (error.message.includes('Failed to fetch')) {
        console.error('Network error - please check your connection');
      }
    } finally {
      setIsLoadingChart(false);
    }
  }, [timeRange, selectedAgentId, isInitialLoad]);

  const fetchStats = useCallback(async () => {
    if (!isInitialLoad) {
      setIsLoadingStats(true);
    }
    try {
      // Modify URL to include agent_id if an agent is selected
      const baseUrl = `${process.env.REACT_APP_API_URL}/api/calls/stats`;
      const url = selectedAgentId === 'all' 
        ? baseUrl
        : `${baseUrl}?agent_id=${selectedAgentId}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      if (response.status === 429) {
        console.warn('Rate limit exceeded. Please wait before making more requests.');
        return;
      }

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const data = await response.json();
      console.log('Received stats data:', data);
      
      // Validate and set default values for missing data
      const validatedStats = {
        totalCalls: data.totalCalls || 0,
        successRate: data.successRate || 0,
        avgDuration: data.avgDuration || 0,
        costPerCall: data.costPerCall || 0,
        totalLeads: data.totalLeads || 0,
        totalEnquiries: data.totalEnquiries || 0,
        avgCallsPerAgent: data.avgCallsPerAgent || 0,
        completionRate: isNaN(Number(data.completionRate)) ? 0 : Number(data.completionRate),
        trends: data.trends || {},
        avgExtractionDuration: data.avgExtractionDuration || 0,
        extractionDurationTrend: data.extractionDurationTrend || 0
      };
      
      console.log('Validated stats:', validatedStats);
      setStats(validatedStats);
    } catch (error) {
      console.error('Error fetching stats:', error);
      if (error.message.includes('Failed to fetch')) {
        console.error('Network error - please check your connection');
      }
    } finally {
      setIsLoadingStats(false);
    }
  }, [selectedAgentId, isInitialLoad]);

  // Initial load
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        await Promise.all([
          fetchStats(),
          fetchChartData(),
          fetchCalls()
        ]);
      } catch (error) {
        console.error('Error fetching initial data:', error);
      } finally {
        setIsInitialLoad(false);  // Set to false after initial load
      }
    };

    fetchInitialData();
  }, [selectedAgentId]);

  // Handle page changes - only fetch calls
  useEffect(() => {
    fetchCalls();
  }, [page, fetchCalls]);

  // Handle time range changes - only fetch chart data
  useEffect(() => {
    fetchChartData();
  }, [timeRange, fetchChartData]);

  const fetchAssignedAgents = async () => {
    try {
      // Fetch regular agents
      const regularResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/agents`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      
      let regularAgents = [];
      if (regularResponse.ok) {
        const regularData = await regularResponse.json();
        if (Array.isArray(regularData)) {
          regularAgents = regularData.map(agent => ({
            ...agent,
            source_type: 'regular',
            display_name: `${agent.name || 'Unnamed Agent'}`
          }));
        }
      }

      // Fetch plugnplay agents
      let plugnplayAgents = [];
      try {
        const plugnplayResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/plugnplay/agents`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          }
        });
        
        if (plugnplayResponse.ok) {
          const plugnplayData = await plugnplayResponse.json();
          if (Array.isArray(plugnplayData)) {
            plugnplayAgents = plugnplayData.map(agent => ({
              ...agent,
              source_type: 'plugnplay',
              display_name: `🎯 ${agent.name || 'Unnamed PlugnPlay Agent'}`
            }));
          }
        }
      } catch (plugnplayError) {
        console.log('No plugnplay agents found or endpoint not available');
      }

      // Combine both types
      const allAgents = [...regularAgents, ...plugnplayAgents];
      setAssignedAgents(allAgents);
      
    } catch (error) {
      console.error('Error fetching assigned agents:', error);
      setAssignedAgents([]);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const url = selectedAgentId === 'all'
        ? `${process.env.REACT_APP_API_URL}/api/dashboard/stats`
        : `${process.env.REACT_APP_API_URL}/api/dashboard/stats/${selectedAgentId}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      if (response.status === 429) {
        console.warn('Rate limit exceeded. Please wait before making more requests.');
        return;
      }

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const data = await response.json();
      setDashboardData(data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Add user-friendly error handling
      if (error.message.includes('Failed to fetch')) {
        console.error('Network error - please check your connection');
      }
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        align: 'start',
        labels: {
          usePointStyle: true,
          padding: 25,
          boxWidth: 8,
          boxHeight: 8,
          font: {
            size: 12,
            weight: 500
          },
          color: '#6B7280',
          generateLabels: (chart) => {
            const datasets = chart.data.datasets;
            return datasets.map((dataset, i) => ({
              text: dataset.label,
              fillStyle: dataset.borderColor,
              strokeStyle: dataset.borderColor,
              lineWidth: 0,
              pointStyle: 'circle',
              hidden: !chart.isDatasetVisible(i),
              index: i
            }));
          }
        }
      },
      customCanvasBackgroundColor: {
        color: 'white',
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 0,
          maxTicksLimit: timeRange === '1' ? 12 : 7,
          padding: 10,
          color: '#6B7280',
          font: {
            size: 11,
            weight: 500
          },
          callback: function(value, index, ticks) {
            const date = new Date(this.getLabelForValue(value));
            if (timeRange === '1') {
              return date.getHours().toString().padStart(2, '0') + ':00';
            }
            return this.getLabelForValue(value);
          }
        },
        border: {
          display: false
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          borderDash: [2],
          drawBorder: false,
          color: alpha('#E5E7EB', 0.1)
        },
        ticks: {
          padding: 10,
          color: '#6B7280',
          font: {
            size: 11,
            weight: 500
          }
        },
        border: {
          display: false
        }
      }
    },
    elements: {
      line: {
        tension: 0.4,
        borderWidth: 1.5, // Thinner lines
        fill: true,
      },
      point: {
        radius: 0,
        hitRadius: 8,
        hoverRadius: 4,
        hoverBorderWidth: 2,
        hoverBackgroundColor: 'white'
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  };

  ChartJS.register(createGradientPlugin);

  const LoadingOverlay = () => (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        zIndex: 1,
      }}
    >
      <CircularProgress size={24} />
    </Box>
  );

  const handlePageChange = (event, newPage) => {
    console.log('Changing to page:', newPage);
    setPage(newPage);
  };

  const parseSummaryContent = (summary) => {
    if (!summary) return null;

    // Split the summary into sections
    const sections = summary.split(/(?=#{1,4}\s)/);
    
    return sections.map((section, index) => {
      if (!section.trim()) return null;
      
      // Check if it's a header
      const headerMatch = section.match(/^(#{1,4})\s*(.+?)(\n|$)/);
      if (headerMatch) {
        const level = headerMatch[1].length;
        const title = headerMatch[2].trim();
        const content = section.replace(/^#{1,4}\s*.+?(\n|$)/, '').trim();
        
        const getHeaderIcon = (title) => {
          const titleLower = title.toLowerCase();
          if (titleLower.includes('overview') || titleLower.includes('statistics')) return <BarChartIcon />;
          if (titleLower.includes('performance') || titleLower.includes('insights')) return <TrendingUpIcon />;
          if (titleLower.includes('themes') || titleLower.includes('topics')) return <PsychologyIcon />;
          if (titleLower.includes('context') || titleLower.includes('specific')) return <InsightsIcon />;
          if (titleLower.includes('recommendation')) return <RecommendIcon />;
          return <AssessmentIcon />;
        };
        
        const getHeaderColor = (level) => {
          switch(level) {
            case 1: return '#1a472a';
            case 2: return '#2e7d32';
            case 3: return '#388e3c';
            case 4: return '#4caf50';
            default: return '#2e7d32';
          }
        };

        return (
          <Box key={index} sx={{ mb: 3 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1.5, 
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.4)',
              border: '1px solid rgba(46, 125, 50, 0.2)'
            }}>
              <Box sx={{ 
                color: getHeaderColor(level),
                display: 'flex',
                alignItems: 'center'
              }}>
                {getHeaderIcon(title)}
              </Box>
              <Typography 
                variant={level === 1 ? 'h6' : level === 2 ? 'subtitle1' : 'subtitle2'} 
                sx={{ 
                  fontWeight: 'bold',
                  color: getHeaderColor(level),
                  textTransform: level === 1 ? 'uppercase' : 'none',
                  letterSpacing: level === 1 ? '0.5px' : 'normal'
                }}
              >
                {title}
              </Typography>
            </Box>
            {content && (
              <Typography 
                variant="body2" 
                sx={{ 
                  lineHeight: 1.6,
                  color: '#1a472a',
                  pl: 2,
                  fontSize: '0.9rem',
                  '& strong': {
                    color: '#2e7d32',
                    fontWeight: 600
                  }
                }}
              >
                {content.split('\n').map((line, lineIndex) => {
                  if (line.trim().startsWith('-')) {
                    return (
                      <Box key={lineIndex} sx={{ display: 'flex', alignItems: 'flex-start', mt: 1, ml: 1 }}>
                        <Box sx={{ color: '#4caf50', mr: 1, mt: 0.2 }}>•</Box>
                        <Typography variant="body2" sx={{ flex: 1, color: '#1a472a' }}>
                          {line.trim().substring(1).trim()}
                        </Typography>
                      </Box>
                    );
                  }
                  return line.trim() ? <span key={lineIndex}>{line}<br /></span> : <br key={lineIndex} />;
                })}
              </Typography>
            )}
          </Box>
        );
      }
      
      // Regular content without header
      return (
        <Typography 
          key={index}
          variant="body2" 
          sx={{ 
            mb: 2,
            lineHeight: 1.6,
            color: '#1a472a',
            fontSize: '0.9rem'
          }}
        >
          {section.trim()}
        </Typography>
      );
    }).filter(Boolean);
  };

  const handleGenerateFAQs = async () => {
    try {
        setIsGenerating(true);
        setIsExpanded(true);
        setProcessingDetails([]);
        setProgress(0);
        setStatusMessage('Starting FAQ generation...');
        
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/calls/most-asked-questions`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
            }
        });
        
        if (!response.ok) {
            throw new Error('Failed to generate FAQs');
        }
        
        const data = await response.json();
        setFaqs(data.faqs || []);
        setProcessingDetails([
            `Analyzed recent calls`,
            `Generated ${data.faqs ? data.faqs.length : 0} FAQs`,
            'FAQ Generation Complete'
        ]);
        setStatusMessage('FAQ Generation Complete');
        setProgress(100);
        setIsGenerating(false);
        
    } catch (error) {
        console.error('Error generating FAQs:', error);
        setStatusMessage('Error generating FAQs');
        setProcessingDetails([`Error: ${error.message}`]);
        setIsGenerating(false);
    }
  };

  // Add an effect to monitor FAQ state changes
  useEffect(() => {
    console.log('FAQs updated:', faqs);
  }, [faqs]);

  useEffect(() => {
    const fetchCompletionStats = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/stats/completion`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const data = await response.json();
        setCompletionRate(data.current_rate);
        setCompletionTrend(data.trend);
      } catch (error) {
        console.error('Error fetching completion stats:', error);
      }
    };
    fetchCompletionStats();
  }, [timeRange]);

  const handleGenerateSummary = async () => {
    try {
      // Validate date range on frontend
      const daysDiff = Math.ceil((summaryEndDate - summaryStartDate) / (1000 * 60 * 60 * 24));
      if (daysDiff > 30) {
        alert('Date range cannot exceed 30 days. Please select a shorter time period.');
        return;
      }
      
      if (summaryStartDate >= summaryEndDate) {
        alert('Start date must be before end date.');
        return;
      }

      setSummaryGenerating(true);
      setSummaryExpanded(true);
      setSummaryProcessingDetails([]);
      setSummaryStatusMessage('Analyzing call data...');
      
      // Simulate realistic loading steps
      const loadingSteps = [
        'Retrieving call records...',
        'Processing transcripts...',
        'Analyzing patterns...',
        'Generating insights...',
        'Finalizing report...'
      ];
      
      let currentStep = 0;
      const updateLoading = () => {
        if (currentStep < loadingSteps.length) {
          setSummaryStatusMessage(loadingSteps[currentStep]);
          setSummaryProcessingDetails(prev => [...prev, loadingSteps[currentStep]]);
          currentStep++;
          setTimeout(updateLoading, 800); // Update every 800ms
        }
      };
      
      // Start the loading animation
      setTimeout(updateLoading, 500);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/calls/comprehensive-summary`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          start_date: summaryStartDate.toISOString(),
          end_date: summaryEndDate.toISOString(),
          agent_id: selectedAgentId === 'all' ? null : selectedAgentId
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to generate comprehensive summary');
      }
      
      const data = await response.json();
      setGeneratedSummary(data.summary || '');
      setSummaryProcessingDetails(prev => [
        ...prev,
        `✅ Analysis complete - ${data.total_calls || 0} calls processed`,
        `📊 Completion Rate: ${(data.completion_rate || 0).toFixed(1)}%`,
        `🎯 Leads: ${data.leads_count || 0} | Enquiries: ${data.enquiries_count || 0}`
      ]);
      setSummaryStatusMessage('Summary Generation Complete');
      setSummaryGenerating(false);
      
    } catch (error) {
      console.error('Error generating summary:', error);
      setSummaryStatusMessage('Error generating summary');
      setSummaryProcessingDetails(prev => [...prev, `❌ Error: ${error.message}`]);
      setSummaryGenerating(false);
    }
  };

  if (isInitialLoading) {
    return <LoadingScreen />;
  }

  return (
    <PageWrapper>
    <Box 
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      sx={{ 
        p: { xs: 2, sm: 3 },
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        '@keyframes fadeIn': {
          '0%': { opacity: 0, transform: 'translateY(10px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' }
        },
        '@keyframes pulse': {
          '0%': { transform: 'scale(1)', opacity: 1 },
          '50%': { transform: 'scale(1.1)', opacity: 0.7 },
          '100%': { transform: 'scale(1)', opacity: 1 }
        }
      }}
    >
      <Typography 
        variant="h4" 
        sx={{ 
          mb: 3,
          color: 'text.primary',
          fontWeight: 600
        }}
      >
        Dashboard
      </Typography>

        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>View Data For</InputLabel>
          <Select
            value={selectedAgentId}
            onChange={(e) => setSelectedAgentId(e.target.value)}
            label="View Data For"
          >
            <MenuItem value="all">
              {isAdmin ? 'All Agents' : 'All My Agents'}
            </MenuItem>
            {Array.isArray(assignedAgents) && assignedAgents.length > 0 ? (
              assignedAgents.map((agent) => (
                <MenuItem 
                  key={agent?.id || `agent-${Math.random()}`} 
                  value={agent?.id}
                  sx={{
                    ...(agent?.source_type === 'plugnplay' && {
                      backgroundColor: 'rgba(63, 158, 255, 0.05)',
                      '&:hover': {
                        backgroundColor: 'rgba(63, 158, 255, 0.1)',
                      }
                    })
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {agent?.source_type === 'plugnplay' && (
                      <Chip 
                        label="Plug & Play" 
                        size="small" 
                        sx={{ 
                          height: 20, 
                          fontSize: '0.65rem',
                          backgroundColor: '#3f9eff',
                          color: 'white'
                        }} 
                      />
                    )}
                    <Typography>
                      {agent?.display_name || agent?.name || 'Unnamed Agent'}
                    </Typography>
                  </Box>
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled value="" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                No agents available
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Box>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          {isLoadingStats ? (
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Total Calls" 
                  value={stats.totalCalls} 
                  trend={stats.trends?.totalCallsTrend}
            delay={0}
          />
        </Grid>
              <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Success Rate" 
                  value={stats.successRate} 
                  trend={stats.trends?.successRateTrend}
            delay={1}
          />
        </Grid>
              <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Avg Duration" 
                  value={stats.avgDuration}
                  trend={stats.trends?.avgDurationTrend}
            delay={2}
          />
        </Grid>
              <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Completion Rate" 
            value={stats.completionRate}
            trend={stats.trends?.completionRateTrend}
            delay={3}
            suffix="%"
          />
        </Grid>
              <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Total Leads" 
                  value={stats.totalLeads}
                  trend={stats.trends?.totalLeadsTrend}
            delay={4}
          />
        </Grid>
              <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Total Enquiries" 
                  value={stats.totalEnquiries}
                  trend={stats.trends?.totalEnquiriesTrend}
            delay={5}
          />
        </Grid>
              <Grid item xs={12} sm={6} md={3}>
          <Box
            className={`summary-generator ${summaryExpanded || summaryGenerating ? 'expanded' : ''}`}
            onClick={!summaryExpanded && !summaryGenerating ? () => setSummaryDialogOpen(true) : undefined}
            sx={{
                backgroundColor: '#b8e6b8 !important',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                padding: '20px',
                borderRadius: '12px',
                cursor: !summaryExpanded && !summaryGenerating ? 'pointer' : 'default',
                transition: 'all 0.3s ease-in-out',
                height: 'auto',
                minHeight: summaryExpanded || summaryGenerating ? '300px' : '100px',
                maxHeight: 'none'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <AssessmentIcon sx={{ color: '#2e7d32', fontSize: 28 }} />
                <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                    Summary Generator
                </Typography>
            </Box>
            
            {!summaryExpanded && !summaryGenerating && (
                <Typography variant="body1" sx={{ textAlign: 'center' }}>
                    Click to generate comprehensive summary
                </Typography>
            )}
            
            {summaryGenerating && (
                <Box sx={{ 
                    width: '100%', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    gap: 2,
                    flex: 1,
                    p: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <CircularProgress size={20} sx={{ color: '#2e7d32' }} />
                        <AutoAwesomeIcon sx={{ color: '#2e7d32', animation: 'pulse 2s infinite' }} />
                    </Box>
                    <Typography variant="body2" sx={{ 
                        color: '#2e7d32', 
                        textAlign: 'center',
                        fontWeight: 500,
                        mb: 2
                    }}>
                        {summaryStatusMessage}
                    </Typography>
                    <Box sx={{ width: '100%', maxHeight: '150px', overflowY: 'auto' }}>
                        {summaryProcessingDetails.map((detail, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    p: 1,
                                    mb: 0.5,
                                    borderRadius: 1,
                                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                    opacity: 0,
                                    animation: 'fadeIn 0.5s forwards',
                                    animationDelay: `${index * 0.3}s`
                                }}
                            >
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        color: '#1a472a',
                                        fontSize: '0.75rem',
                                        fontWeight: 500
                                    }}
                                >
                                    {detail}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {summaryExpanded && !summaryGenerating && (
                <Box sx={{ 
                    mt: 2, 
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    flex: 1,
                    maxHeight: '300px',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.3) 100%)',
                    borderRadius: 2,
                    p: 2,
                    border: '1px solid rgba(46, 125, 50, 0.2)',
                    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'rgba(255,255,255,0.3)',
                        borderRadius: '3px'
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'linear-gradient(180deg, #4caf50, #2e7d32)',
                        borderRadius: '3px',
                        '&:hover': {
                            background: 'linear-gradient(180deg, #66bb6a, #388e3c)'
                        }
                    }
                }}>
                    {generatedSummary ? (
                        <Box sx={{ width: '100%' }}>
                            {parseSummaryContent(generatedSummary)}
                        </Box>
                    ) : (
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            height: '100%',
                            gap: 2
                        }}>
                            <AssessmentIcon sx={{ fontSize: 48, color: '#4caf50', opacity: 0.5 }} />
                            <Typography
                                variant="body1"
                                sx={{
                                    textAlign: 'center',
                                    color: '#2e7d32',
                                    fontWeight: 500
                                }}
                            >
                                No summary available.
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
          </Box>
        </Grid>
              <Grid item xs={12} sm={6} md={3}>
          <Box
            className={`faq-button ${isExpanded || isGenerating ? 'expanded' : ''}`}
            onClick={!isExpanded && !isGenerating ? handleGenerateFAQs : undefined}
            sx={{
                backgroundColor: '#add8e6 !important',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                padding: '20px',
                borderRadius: '12px',
                cursor: !isExpanded && !isGenerating ? 'pointer' : 'default',
                transition: 'all 0.3s ease-in-out',
                height: 'auto',
                minHeight: isExpanded || isGenerating ? '300px' : '100px',
                maxHeight: 'none'
            }}
          >
            <Typography variant="h6" sx={{ color: '#1a365d', mb: 2 }}>
                Frequently Asked Questions
            </Typography>
            
            {!isExpanded && !isGenerating && (
                <Typography variant="body1" sx={{ textAlign: 'center' }}>
                    Click to generate FAQs
                </Typography>
            )}
            
            {isGenerating && (
                <Box sx={{ 
                    width: '100%', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    gap: 3,
                    flex: 1
                }}>
                    <CircularProgress size={24} sx={{ color: '#1a365d' }} />
                    <Box sx={{ width: '80%' }}>
                        <LinearProgress 
                            variant="determinate" 
                            value={progress}
                            sx={{
                                backgroundColor: 'rgba(255,255,255,0.3)',
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: '#1a365d'
                                }
                            }}
                        />
                        <Typography variant="body2" sx={{ color: '#1a365d', mt: 2, textAlign: 'center' }}>
                            {statusMessage}
                        </Typography>
                        <Box sx={{ mt: 3 }}>
                            {processingDetails.map((detail, index) => (
                                <Typography 
                                    key={index} 
                                    variant="caption" 
                                    sx={{ 
                                        display: 'block', 
                                        color: '#1a365d',
                                        mb: 1,
                                        opacity: 0,
                                        animation: 'fadeIn 0.5s forwards',
                                        animationDelay: `${index * 0.2}s`
                                    }}
                                >
                                    {detail}
                                </Typography>
                            ))}
                        </Box>
                    </Box>
                </Box>
            )}

            {isExpanded && !isGenerating && (
                <Box sx={{ 
                    mt: 2, 
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    flex: 1
                }}>
                    {faqs.length > 0 ? (
                        faqs.map((faq, index) => (
                            <Typography
                                key={index}
                                variant="body1"
                                className="faq-item"
                                sx={{
                                    mb: 1,
                                    padding: '12px',
                                    borderRadius: '4px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    opacity: 0,
                                    animation: 'fadeIn 0.5s forwards',
                                    animationDelay: `${index * 0.3}s`
                                }}
                            >
                                {faq}
                            </Typography>
                        ))
                    ) : (
                        <Typography
                            variant="body1"
                            sx={{
                                textAlign: 'center',
                                color: '#1a365d',
                                opacity: 0,
                                animation: 'fadeIn 0.5s forwards',
                                padding: '20px',
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                borderRadius: '4px'
                            }}
                        >
                            No frequently asked questions found in recent calls.
                        </Typography>
                    )}
                </Box>
            )}
          </Box>
        </Grid>
            </>
          )}
      </Grid>

            <Card 
        sx={{ 
            mb: 4,
          p: { xs: 2, sm: 3 },
          width: '100%'
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              color: 'text.primary'
            }}
          >
            Call Analytics
          </Typography>
          <FormControl 
            size="small" 
            sx={{ 
              minWidth: 130,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: 'background.paper',
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                '&:hover': {
                  backgroundColor: alpha('#000', 0.01)
                }
              }
            }}
          >
            <Select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <MenuItem value="1">Last 24 hours</MenuItem>
              <MenuItem value="7">Last 7 days</MenuItem>
              <MenuItem value="30">Last 30 days</MenuItem>
              <MenuItem value="90">Last 90 days</MenuItem>
              <MenuItem value="365">Last year</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ 
          height: { xs: 300, sm: 380 },
          width: '100%',
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
            {isLoadingChart ? (
              <Box sx={{ 
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.8)'
              }}>
                <CircularProgress />
              </Box>
            ) : (
              <Line data={chartData} options={chartOptions} />
            )}
        </Box>
      </Card>

       {/* Date Range Dialog for Summary Generator */}
       <Dialog 
         open={summaryDialogOpen} 
         onClose={() => setSummaryDialogOpen(false)}
         maxWidth="sm"
         fullWidth
       >
         <DialogTitle>Generate Comprehensive Summary</DialogTitle>
         <DialogContent>
           <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
             <LocalizationProvider dateAdapter={AdapterDateFns}>
               <DateTimePicker
                 label="Start Date & Time"
                 value={summaryStartDate}
                 onChange={(newValue) => setSummaryStartDate(newValue)}
                 renderInput={(params) => <TextField {...params} fullWidth />}
               />
               <DateTimePicker
                 label="End Date & Time"
                 value={summaryEndDate}
                 onChange={(newValue) => setSummaryEndDate(newValue)}
                 renderInput={(params) => <TextField {...params} fullWidth />}
               />
             </LocalizationProvider>
             <Typography variant="body2" color="text.secondary">
               Generate a comprehensive summary including completion rates, leads, inquiries, and context-specific insights for the selected date range.
             </Typography>
             <Typography variant="caption" color="warning.main" sx={{ mt: 1 }}>
               ⚠️ Maximum date range: 30 days
             </Typography>
           </Box>
         </DialogContent>
         <DialogActions sx={{ p: 2.5, pt: 0 }}>
           <Button 
             onClick={() => setSummaryDialogOpen(false)}
           >
             Cancel
           </Button>
           <Button
             onClick={() => {
               setSummaryDialogOpen(false);
               handleGenerateSummary();
             }}
             variant="contained"
             sx={{ 
               backgroundColor: '#2e7d32',
               '&:hover': {
                 backgroundColor: '#1b5e20'
               }
             }}
           >
             Generate Summary
           </Button>
         </DialogActions>
       </Dialog>

      <Card sx={{ width: '100%', position: 'relative' }}>
        {isLoadingCalls && <LoadingScreen type="section" />}
        <Box sx={{ 
          opacity: isLoadingCalls ? 0.5 : 1,
          pointerEvents: isLoadingCalls ? 'none' : 'auto'
        }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Recent Calls
            </Typography>
        </Box>
          <TableContainer>
            <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell width="50px" />
                <TableCell width="180px">Date & Time</TableCell>
                <TableCell width="120px">Type</TableCell>
                <TableCell width="150px">Agent</TableCell>
                <TableCell>Topic</TableCell>
                <TableCell width="100px">Status</TableCell>
                <TableCell width="100px" align="right">Duration</TableCell>
                <TableCell width="100px" align="right">Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
                {calls.length > 0 ? (
                  calls.map((call) => (
                    <RecentCallRow key={call.id} call={call} />
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">
                        {isLoadingCalls ? 'Loading calls...' : 'No calls found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
            </TableBody>
          </Table>
        </TableContainer>
          {totalCalls > 0 && (
            <Box sx={{ 
              p: 2, 
              display: 'flex', 
              justifyContent: 'center',
              borderTop: '1px solid',
              borderColor: 'divider'
            }}>
          <Pagination 
            count={totalPages}
                page={page}
                onChange={(_, newPage) => setPage(newPage)}
            color="primary"
            shape="rounded"
            disabled={isLoadingCalls}
                showFirstButton
                showLastButton
                sx={{
                  '& .Mui-disabled': {
                    opacity: 0.5
                  }
                }}
          />
        </Box>
          )}
        </Box>
      </Card>
        
    </Box>
    <FeedbackButton />
    </PageWrapper>
  );
};

export default Dashboard; 
